import { metersPerDegLng } from '../map/mapGeometry.js';
/**
 * The one-line query sent to the geocoder.
 *
 * Only address components are included — no site name, no notes, no program,
 * and structurally nothing else, because this function's parameter type cannot
 * express anything else (docs/google-maps-privacy-boundary.md). Returns null
 * when there is too little to geocode, so the caller shows "enter an address"
 * rather than issuing a request that would resolve to a country's centroid.
 */
export function composeGeocodeQuery(address) {
    const parts = [
        address.addressLine1,
        address.city,
        address.region,
        address.postalCode,
        address.country,
    ]
        .map((p) => (p ?? '').trim())
        .filter((p) => p.length > 0);
    // A postal code or a street line alone is geocodable; a bare country is not.
    const hasSpecific = (address.addressLine1 ?? '').trim().length > 0 ||
        (address.postalCode ?? '').trim().length > 0 ||
        (address.city ?? '').trim().length > 0;
    if (!hasSpecific || parts.length === 0)
        return null;
    return parts.join(', ');
}
/**
 * Parse a typed coordinate. Returns null for anything that is not a finite
 * number inside the valid range — an empty box, a half-typed minus sign, or a
 * latitude of 91 all produce null rather than a number nobody meant.
 */
export function parseLatitude(text) {
    return parseBounded(text, -90, 90);
}
export function parseLongitude(text) {
    return parseBounded(text, -180, 180);
}
function parseBounded(text, min, max) {
    const trimmed = text.trim();
    if (trimmed === '' || trimmed === '-' || trimmed === '.')
        return null;
    const value = Number(trimmed);
    if (!Number.isFinite(value))
        return null;
    if (value < min || value > max)
        return null;
    return value;
}
/** Six decimal places ≈ 0.1 m — the precision the coordinate columns store. */
export function formatCoordinate(value) {
    return value.toFixed(6);
}
const FEET_PER_METER = 3.280839895;
/** work_geofence stores metres; the radius control offers feet as well. */
export function toMeters(value, unit) {
    return unit === 'm' ? Math.round(value) : Math.round(value / FEET_PER_METER);
}
export function fromMeters(meters, unit) {
    return unit === 'm' ? Math.round(meters) : Math.round(meters * FEET_PER_METER);
}
/** Mirrors the work_geofence_radius_ck constraint: a site, never a region. */
export const MIN_RADIUS_METERS = 1;
export const MAX_RADIUS_METERS = 50_000;
export function radiusError(meters) {
    if (meters == null || !Number.isFinite(meters))
        return 'Enter a radius.';
    if (meters < MIN_RADIUS_METERS)
        return 'A radius must be at least 1 metre.';
    if (meters > MAX_RADIUS_METERS)
        return 'A radius must be 50 km or less — this is a site, not a region.';
    return null;
}
/**
 * Move a pin by a fixed distance without a mouse.
 *
 * Dragging is the fast way to place a marker; it must not be the ONLY way. This
 * gives the same capability to a keyboard, and — with the coordinate boxes — to
 * anyone who would rather type an exact position than aim at a map.
 *
 * Latitude is clamped to the poles; longitude wraps, so nudging east across the
 * antimeridian stays a real place rather than becoming an invalid coordinate.
 */
export function nudge(position, direction, stepMeters) {
    const metersPerDegLat = 111_320;
    const latStep = stepMeters / metersPerDegLat;
    // metersPerDegLng already floors cos(latitude), so a pole cannot produce a
    // division by zero here — the step simply becomes large, and the wrap below
    // keeps the result a real longitude.
    const lngStep = stepMeters / metersPerDegLng(position.latitude);
    switch (direction) {
        case 'north':
            return { ...position, latitude: Math.min(90, position.latitude + latStep) };
        case 'south':
            return { ...position, latitude: Math.max(-90, position.latitude - latStep) };
        case 'east':
            return { ...position, longitude: wrapLongitude(position.longitude + lngStep) };
        case 'west':
            return { ...position, longitude: wrapLongitude(position.longitude - lngStep) };
    }
}
export function wrapLongitude(lng) {
    let v = lng;
    while (v > 180)
        v -= 360;
    while (v < -180)
        v += 360;
    return v;
}
export function geocodeFeedback(outcome, matchCount = 0) {
    switch (outcome) {
        case 'ok':
            return {
                tone: 'good',
                message: 'Address found. Check the pin and adjust it if needed.',
                usable: true,
            };
        case 'ambiguous':
            return {
                tone: 'warn',
                message: `That address matched ${matchCount} places. The closest is shown — move the pin if it is the wrong one.`,
                usable: true,
            };
        case 'not_found':
            return {
                tone: 'warn',
                message: 'We couldn’t find that address. Check it, or place the pin yourself and set the position by hand.',
                usable: false,
            };
        case 'denied':
            return {
                tone: 'error',
                message: 'Address lookup isn’t available for this site right now. You can still set the position by hand.',
                usable: false,
            };
        case 'failed':
            return {
                tone: 'error',
                message: 'Address lookup didn’t respond. Try again, or set the position by hand.',
                usable: false,
            };
    }
}
/** Plain-language provenance, for the line under the coordinates. */
export function coordinateProvenanceLabel(source, setAt) {
    const when = setAt ? ` on ${new Date(setAt).toLocaleDateString()}` : '';
    switch (source) {
        case 'geocoded':
            return `Found from the address${when}`;
        case 'manual':
            return `Placed by hand${when}`;
        case 'imported':
            return `Brought in with your data${when}`;
        default:
            return 'No position set';
    }
}
//# sourceMappingURL=sitePickerLogic.js.map